import { useState } from "react";
import {
    Title, Group, Button, TextInput, Select, Table, ActionIcon, Stack, Text, Card, Badge, Menu, Tabs, Tooltip,
} from "@mantine/core";
import { IconPlus, IconSearch, IconDots, IconPencil, IconTrash, IconHistory, IconEye, IconArrowRight } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { classifyRisk, RisikoStatus, RiskClass, type Risiko, type Locale } from "@iso27001/shared";
import { risikenApi } from "../../api/risiken.js";
import { RisikoFormModal } from "./RisikoFormModal.js";
import { AuditHistoryModal } from "../../components/AuditHistoryModal.js";
import { useAuth } from "../../auth/AuthContext.js";
import { MatrixGrid } from "./MatrixGrid.js";
import { formatDate } from "../../i18n/format-date.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useRecordDialog } from "../../components/use-record-dialog.js";
import { rowOpenProps, stopRowOpen, cellBadgeStyles, nowrap } from "../../components/list-table.js";
import { isRisikoClosed } from "../../components/record-state.js";
import { risikoStatusColor, riskClassColor } from "../../components/status-colors.js";
import { useUserName } from "../../components/use-user-name.js";

export function RisikenListPage() {
    const { user } = useAuth();
    const canEdit = user?.role === "admin" || user?.role === "editor";
    const qc = useQueryClient();
    const { t, i18n } = useTranslation();
    const locale = i18n.language as Locale;
    const { label } = useEnumLabel();
    const userName = useUserName();

    const statusFilterOptions = [
        { value: "", label: t("common.filter.allStatus") },
        ...RisikoStatus.options.map((value) => ({ value, label: label("risikoStatus", value) })),
    ];
    const classFilterOptions = [
        { value: "", label: t("risiken.list.filter.allClasses") },
        ...RiskClass.options.map((value) => ({ value, label: label("riskClass", value) })),
    ];

    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [classFilter, setClassFilter] = useState<string>("");
    const [auditId, setAuditId] = useState<number | null>(null);
    const dialog = useRecordDialog<Risiko>();

    const list = useQuery({
        queryKey: ["risiken", { q, status: statusFilter, riskClass: classFilter }],
        queryFn: () => risikenApi.list({
            q: q || undefined,
            status: (statusFilter || undefined) as RisikoStatus | undefined,
            riskClass: (classFilter || undefined) as RiskClass | undefined,
            pageSize: 200,
        }),
    });

    const removeMut = useMutation({
        mutationFn: (id: number) => risikenApi.remove(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["risiken"] });
            notifications.show({ message: t("risiken.list.notify.deleted"), color: "teal" });
        },
    });

    const onDelete = (r: Risiko) => {
        modals.openConfirmModal({
            title: t("risiken.list.confirmDelete.title"),
            children: <Text size="sm">{t("risiken.list.confirmDelete.message", { code: r.code })}</Text>,
            labels: { confirm: t("common.action.delete"), cancel: t("common.action.cancel") },
            confirmProps: { color: "red" },
            onConfirm: () => removeMut.mutate(r.id),
        });
    };

    return (
        <Stack>
            <Group justify="space-between">
                <Title order={2}>{t("risiken.list.title")}</Title>
                {canEdit && (
                    <Button leftSection={<IconPlus size={16} />} onClick={() => dialog.open(null)}>
                        {t("risiken.newRisk")}
                    </Button>
                )}
            </Group>

            <Tabs defaultValue="liste">
                <Tabs.List>
                    <Tabs.Tab value="liste">{t("risiken.list.tab.list")}</Tabs.Tab>
                    <Tabs.Tab value="matrix">{t("risiken.list.tab.matrix")}</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="liste" pt="md">
                    <Stack>
                        <Card withBorder padding="xs">
                            <Group>
                                <TextInput
                                    placeholder={t("common.search.placeholder")}
                                    leftSection={<IconSearch size={14} />}
                                    value={q}
                                    onChange={(e) => setQ(e.currentTarget.value)}
                                    style={{ flex: 1, maxWidth: 320 }}
                                />
                                <Select
                                    data={statusFilterOptions}
                                    value={statusFilter}
                                    onChange={(val) => setStatusFilter(val ?? "")}
                                    style={{ width: 180 }}
                                    placeholder={t("common.filter.allStatus")}
                                />
                                <Select
                                    data={classFilterOptions}
                                    value={classFilter}
                                    onChange={(val) => setClassFilter(val ?? "")}
                                    style={{ width: 160 }}
                                    placeholder={t("risiken.list.filter.allClasses")}
                                />
                                <Text size="sm" c="dimmed">{list.data ? t("common.entriesCount", { count: list.data.total }) : ""}</Text>
                            </Group>
                        </Card>
                        <Card withBorder padding={0}>
                            <Table striped highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th style={nowrap}>{t("common.field.code")}</Table.Th>
                                        <Table.Th style={nowrap}>{t("common.field.date")}</Table.Th>
                                        <Table.Th>{t("risiken.list.column.risk")}</Table.Th>
                                        <Table.Th style={nowrap}>{t("risiken.list.column.score")}</Table.Th>
                                        <Table.Th style={nowrap}>{t("risiken.field.treatment")}</Table.Th>
                                        <Table.Th style={nowrap}>{t("risiken.field.riskOwner")}</Table.Th>
                                        <Table.Th style={nowrap}>{t("common.field.status")}</Table.Th>
                                        <Table.Th style={{ width: 48 }} />
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {list.data?.items.map((r) => {
                                        const classBefore = r.riskClassBefore ?? classifyRisk(r.riskScoreBefore ?? null);
                                        const classAfter = r.riskClassAfter ?? classifyRisk(r.riskScoreAfter ?? null);
                                        return (
                                            <Table.Tr key={r.id} {...rowOpenProps(() => dialog.open(r))}>
                                                <Table.Td>
                                                    <Text ff="monospace" size="sm" style={nowrap}>{r.code}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" style={nowrap}>{formatDate(r.identifiedAt, locale)}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    {/*
                                                      Threat and asset in one cell, on two lines: as separate
                                                      columns beside eight others, both were cut to "Daten…".
                                                    */}
                                                    <Text size="sm" lineClamp={1}>
                                                        {r.bedrohungName ?? r.threatDescription ?? "—"}
                                                    </Text>
                                                    <Text size="xs" c="dimmed" lineClamp={1}>
                                                        {r.primaryAssetName ?? r.primaryAssetText ?? ""}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    {/* Before → after treatment; the levels behind each score are in the tooltip. */}
                                                    <Group gap={6} wrap="nowrap">
                                                        {r.riskScoreBefore != null && classBefore != null ? (
                                                            <Tooltip label={t("risiken.list.scoreBeforeTooltip", { likelihood: r.likelihoodBeforeLabel, damage: r.damageBeforeLabel })}>
                                                                <Badge color={riskClassColor[classBefore]} variant="light" styles={cellBadgeStyles}>
                                                                    {r.riskScoreBefore} · {label("riskClass", classBefore)}
                                                                </Badge>
                                                            </Tooltip>
                                                        ) : (
                                                            <Text size="sm" c="dimmed">—</Text>
                                                        )}
                                                        {r.riskScoreAfter != null && classAfter != null && (
                                                            <>
                                                                <IconArrowRight size={14} color="var(--mantine-color-dimmed)" />
                                                                <Tooltip label={t("risiken.list.scoreAfterTooltip", { likelihood: r.likelihoodAfterLabel, damage: r.damageAfterLabel })}>
                                                                    <Badge color={riskClassColor[classAfter]} variant="light" styles={cellBadgeStyles}>
                                                                        {r.riskScoreAfter} · {label("riskClass", classAfter)}
                                                                    </Badge>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" style={nowrap}>{label("treatment", r.treatment)}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" style={nowrap}>{userName(r.riskOwnerUserId)}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge color={risikoStatusColor[r.status]} variant="light" styles={cellBadgeStyles}>{label("risikoStatus", r.status)}</Badge>
                                                </Table.Td>
                                                <Table.Td {...stopRowOpen}>
                                                    <Menu position="bottom-end">
                                                        <Menu.Target>
                                                            <ActionIcon variant="subtle" aria-label={t("common.action.actions")}><IconDots size={16} /></ActionIcon>
                                                        </Menu.Target>
                                                        <Menu.Dropdown>
                                                            {canEdit && !isRisikoClosed(r.status) ? (
                                                                <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => dialog.open(r)}>
                                                                    {t("common.action.edit")}
                                                                </Menu.Item>
                                                            ) : (
                                                                <Menu.Item leftSection={<IconEye size={14} />} onClick={() => dialog.open(r)}>
                                                                    {t("common.action.open")}
                                                                </Menu.Item>
                                                            )}
                                                            <Menu.Item
                                                                leftSection={<IconHistory size={14} />}
                                                                onClick={() => setAuditId(r.id)}
                                                            >
                                                                {t("common.action.history")}
                                                            </Menu.Item>
                                                            {canEdit && (
                                                                <Menu.Item
                                                                    color="red"
                                                                    leftSection={<IconTrash size={14} />}
                                                                    onClick={() => onDelete(r)}
                                                                >
                                                                    {t("common.action.delete")}
                                                                </Menu.Item>
                                                            )}
                                                        </Menu.Dropdown>
                                                    </Menu>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                    {list.data?.items.length === 0 && (
                                        <Table.Tr>
                                            <Table.Td colSpan={8}>
                                                <Text ta="center" c="dimmed" py="md">{t("risiken.list.empty")}</Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    )}
                                </Table.Tbody>
                            </Table>
                        </Card>
                    </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="matrix" pt="md">
                    <MatrixGrid />
                </Tabs.Panel>
            </Tabs>

            <RisikoFormModal key={dialog.key} opened={dialog.opened} onClose={dialog.close} initial={dialog.record} />
            <AuditHistoryModal
                opened={auditId !== null}
                onClose={() => setAuditId(null)}
                queryKey={["risiken", "audit", auditId]}
                fetchFn={() => risikenApi.audit(auditId!)}
            />
        </Stack>
    );
}
